/**
 * Session Storage Service
 * Manages bill sessions in localStorage for sharing between users
 */

const SESSION_STORAGE_KEY = 'splitbill_sessions';

/**
 * Save a session to localStorage
 */
export const saveSession = (sessionId, billData) => {
  try {
    const sessions = getAllSessions();
    sessions[sessionId] = {
      ...billData,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
    return true;
  } catch (error) {
    console.error('Error saving session:', error);
    return false;
  }
};

/**
 * Get a session from localStorage
 */
export const getSession = (sessionId) => {
  try {
    const sessions = getAllSessions();
    return sessions[sessionId] || null;
  } catch (error) {
    console.error('Error getting session:', error);
    return null;
  }
};

/**
 * Get all sessions
 */
export const getAllSessions = () => {
  try {
    const sessionsData = localStorage.getItem(SESSION_STORAGE_KEY);
    return sessionsData ? JSON.parse(sessionsData) : {};
  } catch (error) {
    console.error('Error getting all sessions:', error);
    return {};
  }
};

/**
 * Delete a session
 */
export const deleteSession = (sessionId) => {
  try {
    const sessions = getAllSessions();
    delete sessions[sessionId];
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
    return true;
  } catch (error) {
    console.error('Error deleting session:', error);
    return false;
  }
};

/**
 * Update session participants
 */
export const updateSessionParticipants = (sessionId, participants) => {
  try {
    const session = getSession(sessionId);
    if (!session) return false;

    session.participants = participants;
    session.lastUpdated = new Date().toISOString();

    const sessions = getAllSessions();
    sessions[sessionId] = session;
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
    return true;
  } catch (error) {
    console.error('Error updating participants:', error);
    return false;
  }
};

/**
 * Clean old sessions (older than 24 hours)
 */
export const cleanOldSessions = () => {
  try {
    const sessions = getAllSessions();
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    Object.keys(sessions).forEach(sessionId => {
      const session = sessions[sessionId];
      if (new Date(session.createdAt) < oneDayAgo) {
        delete sessions[sessionId];
      }
    });

    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
    return true;
  } catch (error) {
    console.error('Error cleaning old sessions:', error);
    return false;
  }
};

/**
 * Get the current active session ID from localStorage
 * Returns the most recently updated session, or null if no sessions exist
 */
export const getCurrentSessionId = () => {
  try {
    const sessions = getAllSessions();
    const sessionIds = Object.keys(sessions);

    if (sessionIds.length === 0) {
      return null;
    }

    // Find the most recently updated session
    let mostRecentSessionId = sessionIds[0];
    let mostRecentDate = new Date(sessions[mostRecentSessionId].lastUpdated);

    sessionIds.forEach(sessionId => {
      const sessionDate = new Date(sessions[sessionId].lastUpdated);
      if (sessionDate > mostRecentDate) {
        mostRecentDate = sessionDate;
        mostRecentSessionId = sessionId;
      }
    });

    return mostRecentSessionId;
  } catch (error) {
    console.error('Error getting current session ID:', error);
    return null;
  }
};

// ============================================
// GESTIÓN DE USUARIO ACTIVO
// ============================================

const ACTIVE_USER_KEY = 'splitbill_active_user';
const ACTIVE_SESSION_KEY = 'splitbill_active_session';

/**
 * Guarda el usuario activo de la sesión actual
 */
export const setCurrentUser = (sessionId, userId, userName, isHost = false) => {
  try {
    const userData = {
      sessionId,
      userId,
      userName,
      isHost,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(ACTIVE_USER_KEY, JSON.stringify(userData));
    localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
    console.log('💾 Usuario activo guardado:', userData);
    return true;
  } catch (error) {
    console.error('Error saving current user:', error);
    return false;
  }
};

/**
 * Obtiene el usuario activo de la sesión actual
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem(ACTIVE_USER_KEY);
    if (!userData) return null;

    const parsed = JSON.parse(userData);
    console.log('📖 Usuario activo recuperado:', parsed);
    return parsed;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Obtiene el ID de la sesión activa guardada
 */
export const getActiveSessionId = () => {
  try {
    return localStorage.getItem(ACTIVE_SESSION_KEY);
  } catch (error) {
    console.error('Error getting active session ID:', error);
    return null;
  }
};

/**
 * Limpia el usuario activo
 */
export const clearCurrentUser = () => {
  try {
    localStorage.removeItem(ACTIVE_USER_KEY);
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    console.log('🧹 Usuario activo limpiado');
    return true;
  } catch (error) {
    console.error('Error clearing current user:', error);
    return false;
  }
};

/**
 * Limpia todas las sesiones y el usuario activo (reset completo)
 */
export const clearAllSessionData = () => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_USER_KEY);
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    console.log('🧹 Todos los datos de sesión limpiados');
    return true;
  } catch (error) {
    console.error('Error clearing all session data:', error);
    return false;
  }
};

/**
 * Verifica si el usuario activo pertenece a la sesión indicada
 */
export const isUserInSession = (sessionId) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) return false;
    return currentUser.sessionId === sessionId;
  } catch (error) {
    console.error('Error checking user session:', error);
    return false;
  }
};
